import React from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SearchSvg from '../other/svg/SearchSvg'
import AuctionSvg from '../other/svg/AuctionSvg'
import LaptopSvg from '../other/svg/LaptopSvg'
import InRealizationSvg from '../other/svg/InRealizationSvg'
import ArchiveSvg from '../other/svg/ArchiveSvg'
import InvoiceSvg from '../other/svg/InvoiceSvg'
import MessagesSvg from '../other/svg/MessagesSvg'
import CashSvg from '../other/svg/CashSvg'
import SettingsSvg from '../other/svg/SettingsSvg'
import LogoutSvg from '../other/svg/LogoutSvg'
import NewMenuItem from '../atoms/NewMenuItem'
import MotoSvg from '../other/svg/MotoSvg'
import ClientSvg from '../other/svg/ClientSvg'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducer } from 'src/redux/rootReducer'
import {
  SET_IS_PURCHASE_MENU_OPEN,
  SET_IS_SELLING_VEHICLES_MENU_OPEN,
  SET_IS_SETTINGS_MENU_OPEN
} from 'src/redux/layoutReducer'
import { SIGN_OUT } from 'dealer-reducers'
import PasswordSvg from '../other/svg/PasswordSvg'
import LanguageSvg from '../other/svg/LanguageSvg'
import Icon from '../atoms/Icon'
import PlatformTerms from './PlatformTerms'

const NewSidebar = (): JSX.Element => {
  const { pathname } = useLocation()
  const { t } = useTranslation()

  const isSellingVehicles = pathname.includes('/selling/vehicles')
  const isSellingClients = pathname.includes('/selling/clients')
  const isSellingInvoices = pathname.includes('/selling/invoices')
  const isSellingIntegrations = pathname.includes('/selling/integrations')
  const isPurchasingConversations = pathname.includes('/purchasing/conversations')
  const isPurchasingBets = pathname.includes('/purchasing/my-bets')
  const isPurchasingRealization = pathname.includes('/purchasing/realization')
  const isPurchasingArchived = pathname.includes('/purchasing/archived')
  const isPurchasingInvoices = pathname.includes('/purchasing/invoices')
  const isArchivedOnPlatform = pathname.includes('/archived-on-platform')
  const isBrowsing = pathname.includes('/browsing/browse')
  const isSettingsProfile = pathname.includes('/settings/profile')
  const isSettingsPassword = pathname.includes('/settings/password')
  const isSettingsLanguage = pathname.includes('/settings/language')
  const dispatch = useDispatch()
  const isPurchaseMenuOpen = useSelector((state: RootReducer) => state.layoutReducer.isPurchaseMenuOpen)
  const isSettingsMenuOpen = useSelector((state: RootReducer) => state.layoutReducer.isSettingsMenuOpen)
  const isSellingVehiclesMenuOpen = useSelector((state: RootReducer) => state.layoutReducer.isSellingVehiclesMenuOpen)
  const purchaseNotificationsCount = useSelector(
    (state: RootReducer) => state.inAppNotificationsReducer.purchase_module.overall
  )
  const betsNotificationsCount = useSelector(
    (state: RootReducer) => state.inAppNotificationsReducer.purchase_module.bets
  )
  const messagesNotificationsCount = useSelector(
    (state: RootReducer) => state.inAppNotificationsReducer.purchase_module.messages
  )
  const invoicesNotificationsCount = useSelector(
    (state: RootReducer) => state.inAppNotificationsReducer.purchase_module.invoices
  )
  const realizationNotificationsCount = useSelector(
    (state: RootReducer) => state.inAppNotificationsReducer.purchase_module.in_realization
  )
  const sellModuleUnlocked = useSelector((state: RootReducer) => state.userDataReducer.sell_module_unlocked)

  return (
    <div className="min-w-[235px] w-[264px] h-[calc(100vh-100px)] rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex justify-between items-center overflow-scroll">
      <div className="flex-1 self-stretch bg-neutral-50 inline-flex flex-col justify-between items-start">
        <div data-type="Line" className="self-stretch h-px px-3 bg-borders-border-base" />
        <div className="self-stretch flex-1 px-3 pt-3 pb-6 flex flex-col justify-start items-start gap-2">
          <NewMenuItem
            active={isBrowsing}
            icon={<SearchSvg />}
            label={t('bottom_menu.search')}
            path="/browsing/browse"
          />

          <NewMenuItem
            isOpen={isPurchaseMenuOpen}
            setIsOpen={(isOpen) => dispatch(SET_IS_PURCHASE_MENU_OPEN(isOpen))}
            active={false}
            icon={
              <Icon
                color="white"
                backgroundColor="errorRed"
                notificationsCount={purchaseNotificationsCount}
                borderColor="white"
                circleTop={-8}
                circleRight={-8}
                style={{ cursor: 'pointer', overflow: 'show' }}
                iconComponent={<AuctionSvg style={{ width: '20px', height: '20px' }} />}
              />
            }
            label={t('my_auctions')}
            nestedLinks={[
              {
                icon: (
                  <Icon
                    color="white"
                    backgroundColor="errorRed"
                    notificationsCount={messagesNotificationsCount}
                    borderColor="white"
                    circleTop={-8}
                    circleRight={-8}
                    iconComponent={<MessagesSvg style={{ width: '15px', height: '15px' }} />}
                  />
                ),
                label: t('messages'),
                path: '/purchasing/conversations',
                active: isPurchasingConversations
              },
              {
                icon: (
                  <Icon
                    color="white"
                    backgroundColor="errorRed"
                    notificationsCount={betsNotificationsCount}
                    borderColor="white"
                    circleTop={-8}
                    circleRight={-8}
                    iconComponent={<LaptopSvg style={{ width: '15px', height: '20px' }} />}
                  />
                ),
                label: t('bets_table_title'),
                path: '/purchasing/my-bets',
                active: isPurchasingBets
              },
              {
                icon: (
                  <Icon
                    color="white"
                    backgroundColor="errorRed"
                    notificationsCount={realizationNotificationsCount}
                    borderColor="white"
                    circleTop={-6}
                    circleRight={-6}
                    iconComponent={<InRealizationSvg style={{ width: '20px', height: '20px' }} />}
                  />
                ),
                label: t('in_realization_table'),
                path: '/purchasing/realization',
                active: isPurchasingRealization
              },
              {
                icon: (
                  <Icon
                    color="white"
                    backgroundColor="errorRed"
                    notificationsCount={invoicesNotificationsCount}
                    borderColor="white"
                    circleTop={-8}
                    circleRight={-8}
                    iconComponent={<InvoiceSvg style={{ width: '18px', height: '18px' }} />}
                  />
                ),
                label: t('invoices'),
                path: '/purchasing/invoices',
                active: isPurchasingInvoices
              },
              {
                icon: (
                  <Icon
                    color="white"
                    backgroundColor="errorRed"
                    notificationsCount={0}
                    borderColor="white"
                    circleTop={-8}
                    circleRight={-8}
                    iconComponent={<ArchiveSvg style={{ width: '18px', height: '18px' }} />}
                  />
                ),
                label: t('sold_tab_title'),
                path: '/purchasing/archived',
                active: isPurchasingArchived
              },
              {
                icon: (
                  <Icon
                    color="white"
                    backgroundColor="errorRed"
                    notificationsCount={0}
                    borderColor="white"
                    circleTop={-8}
                    circleRight={-8}
                    iconComponent={<MotoSvg style={{ width: '18px', height: '18px' }} />}
                  />
                ),
                label: t('sold'),
                path: '/archived-on-platform',
                active: isArchivedOnPlatform
              }
            ]}
          />
          <NewMenuItem
            disabled={!sellModuleUnlocked}
            isOpen={isSellingVehiclesMenuOpen}
            active={false}
            setIsOpen={(isOpen) => dispatch(SET_IS_SELLING_VEHICLES_MENU_OPEN(isOpen))}
            icon={<CashSvg style={{ width: '15px', height: '15px' }} />}
            label={t('bottom_menu.sell')}
            nestedLinks={[
              {
                icon: <MotoSvg style={{ width: '18px', height: '18px' }} />,
                label: t('my_vehicles.title'),
                path: '/selling/vehicles',
                active: isSellingVehicles
              },
              {
                icon: <ClientSvg style={{ width: '18px', height: '18px' }} />,
                label: t('clients.title'),
                path: '/selling/clients',
                active: isSellingClients
              },
              {
                icon: <InvoiceSvg style={{ width: '18px', height: '18px' }} />,
                label: t('invoice_form.sales_invoices'),
                path: '/selling/invoices',
                active: isSellingInvoices
              },
              {
                icon: <LaptopSvg style={{ width: '18px', height: '18px' }} />,
                label: t('integrations'),
                path: '/selling/integrations',
                active: isSellingIntegrations
              }
            ]}
          />
        </div>
        <div className="self-stretch p-3 flex flex-col justify-start items-start gap-2">
          <NewMenuItem
            isOpen={isSettingsMenuOpen}
            setIsOpen={(isOpen) => dispatch(SET_IS_SETTINGS_MENU_OPEN(isOpen))}
            active={false}
            icon={<SettingsSvg />}
            label={t('profile_settings_screen_header')}
            nestedLinks={[
              {
                icon: <InvoiceSvg style={{ width: '18px', height: '18px' }} />,
                label: t('profile'),
                path: '/settings/profile',
                active: isSettingsProfile
              },
              {
                icon: <PasswordSvg style={{ width: '18px', height: '18px' }} />,
                label: t('set_password'),
                path: '/settings/password',
                active: isSettingsPassword
              },
              {
                icon: <LanguageSvg style={{ width: '18px', height: '18px' }} />,
                label: t('set_language'),
                path: '/settings/language',
                active: isSettingsLanguage
              }
            ]}
          />
          <div
            className="self-stretch px-1.5 py-2 rounded-md inline-flex justify-start items-center gap-2 cursor-pointer"
            onClick={() => dispatch(SIGN_OUT())}>
            <div className="w-6 h-6 flex justify-center items-center">
              <div className="relative">
                <LogoutSvg style={{ color: '#52525B', width: '20px', height: '20px' }} />
              </div>
            </div>
            <div className="flex-1 justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-tight">
              {t('log_out')}
            </div>
          </div>
          <PlatformTerms />
        </div>
      </div>
    </div>
  )
}

export default NewSidebar
